import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const VIEW_BOX_SIZE = 100;

const posMap = {
  top: -90,
  right: 0,
  bottom: 90,
  left: 180,
};

/**
 * CircularProgress
 */
export default function CircularProgress(props) {
  const {
    infoFormat,
    className,
    value,
    max,
    min,
    prefixCls,
    showInfo,
    strokeWidth,
    size,
    style,
    gapDegree,
    gapPosition,
    showShadow,
    pathProps,
    pathColor,
    color,
    ...other
  } = props;
  const classes = classNames(
    prefixCls,
    {
      [`${prefixCls}-color-${color}`]: value !== max,
      [`${prefixCls}-color-success`]: value === max,
    },
    className,
  );
  const radius = (VIEW_BOX_SIZE - strokeWidth - 8) / 2;
  const trailLength = (2 * Math.PI * radius * (360 - gapDegree)) / 360;
  const trailStyle = {
    strokeDasharray: Math.round(trailLength * 1000) / 1000,
    transform: `rotateZ(${posMap[gapPosition] + gapDegree / 2}deg)`,
  };
  const pathStyle = {
    strokeDashoffset: `${Math.round(((max - value) / (max - min)) * trailLength * 1000) / 1000}px`,
    ...trailStyle,
    ...(pathProps.style || {}),
  };
  if (pathColor) {
    pathStyle.stroke = pathColor;
  }
  return (
    <div
      className={classes}
      style={{ width: size, height: size, ...style }}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      {...other}>
      <svg viewBox={`0 0 ${VIEW_BOX_SIZE} ${VIEW_BOX_SIZE}`}>
        <circle
          style={trailStyle}
          className={`${prefixCls}-trail`}
          cx={VIEW_BOX_SIZE / 2}
          cy={VIEW_BOX_SIZE / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}/>
        {showShadow ? (
          <filter id="dropshadow" height="150%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
            <feOffset dx="-1" dy="1" result="offsetblur" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.2" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        ) : null}
        <circle
          style={pathStyle}
          className={`${prefixCls}-path`}
          cx={VIEW_BOX_SIZE / 2}
          cy={VIEW_BOX_SIZE / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          filter={showShadow ? 'url(#dropshadow)' : undefined}
          {...pathProps}/>
      </svg>
      {showInfo ? <div className={`${prefixCls}-info`}>{infoFormat(value)}</div> : null}
    </div>
  );
}
CircularProgress.propTypes = {
  prefixCls: PropTypes.string,
  className: PropTypes.string,
  /** 当前值 */
  value: PropTypes.number,
  /** 最大值 */
  max: PropTypes.number,
  /** 最小值 */
  min: PropTypes.number,
  /** 进度信息 function(percent) => node */
  infoFormat: PropTypes.func,
  /** 是否显示进度信息 */
  showInfo: PropTypes.bool,
  /** 宽度 */
  strokeWidth: PropTypes.number,
  /** 半径 */
  radius: PropTypes.number,
  /** 缺口位置 */
  gapPosition: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  /** 缺口大小（单位：角度） */
  gapDegree: PropTypes.number,
  /** 显示阴影 */
  showShadow: PropTypes.bool,
  /** 进度条元素属性 */
  pathProps: PropTypes.object,
  /** 进度条颜色 */
  pathColor: PropTypes.string,
  // showTrail: PropTypes.bool,
  color: PropTypes.oneOf(['primary', 'success', 'warning', 'info', 'error']),
};

CircularProgress.defaultProps = {
  prefixCls: 'ne-circular-progress',
  value: 0,
  infoFormat: percent => `${percent}%`,
  showInfo: true,
  strokeWidth: 4,
  max: 100,
  min: 0,
  size: 100,
  gapDegree: 0,
  gapPosition: 'top',
  showShadow: true,
  pathColor: '',
  color: 'primary',
  pathProps: {},
};



// WEBPACK FOOTER //
// ./src/progress/CircularProgress.jsx